import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { typeLabels, type Movie } from '@/mocks/movies';

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setIndex((prev) => (prev + 1) % items.length), [items.length]);
  const prev = () => setIndex((prev) => (prev - 1 + items.length) % items.length);

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setInterval(next, 6500);
    return () => window.clearInterval(timer);
  }, [paused, next]);

  const active = items[index];

  return (
    <section
      className="relative w-full h-[460px] md:h-[600px] overflow-hidden bg-foreground-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="热门影视焦点轮播"
    >
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            className={`w-full h-full object-cover object-top ${i === index ? 'animate-slow-zoom' : ''}`}
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/10"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30"></div>

      <div className="relative w-full h-full px-4 md:px-8 pb-10 md:pb-14 flex items-end">
        <div key={active.id} className="w-full max-w-2xl text-white animate-fade-up">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="px-2.5 py-1 rounded bg-primary-500 text-background-50 text-xs font-bold">
              {typeLabels[active.type]}
            </span>
            <span className="px-2.5 py-1 rounded bg-white/15 text-white text-xs backdrop-blur-sm">
              {active.status}
            </span>
            <span className="text-xs text-white/80 font-label">
              {active.year} · {active.region} · {active.episodes}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-black tracking-tight leading-tight">
            {active.title}
          </h1>
          <p className="mt-3 text-sm md:text-base text-white/80 leading-relaxed line-clamp-2 max-w-xl">
            {active.summary}
          </p>
          <div className="mt-4 flex items-center gap-4 text-xs md:text-sm text-white/75 flex-wrap">
            <span className="flex items-center gap-1.5">
              <i className="ri-star-fill text-accent-500"></i>
              <span className="font-label font-bold text-white">{active.rating.toFixed(1)}</span> 评分
            </span>
            <span className="w-px h-3 bg-white/25 hidden sm:block"></span>
            <span>导演 {active.director}</span>
            <span className="w-px h-3 bg-white/25 hidden sm:block"></span>
            <span className="truncate">主演 {active.cast.join(' / ')}</span>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="px-6 h-11 inline-flex items-center gap-2 rounded-md bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-play-fill text-base"></i>
              立即观看详情
            </Link>
            <Link
              to={`/category/${active.type}`}
              className="px-6 h-11 inline-flex items-center gap-2 rounded-md bg-white/12 text-white text-sm font-medium border border-white/30 hover:bg-white/20 transition-colors whitespace-nowrap backdrop-blur-sm"
            >
              更多{typeLabels[active.type]}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 hidden md:flex flex-col gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`切换到 ${item.title}`}
            className={`w-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'h-8 bg-primary-500' : 'h-3 bg-white/40 hover:bg-white/70'
            }`}
          />
        ))}
      </div>

      <div className="absolute bottom-5 right-4 md:right-8 flex items-center gap-2">
        <button
          type="button"
          onClick={prev}
          aria-label="上一部"
          className="w-9 h-9 rounded-md bg-white/15 border border-white/25 text-white flex items-center justify-center hover:bg-white/25 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-lg"></i>
        </button>
        <button
          type="button"
          onClick={next}
          aria-label="下一部"
          className="w-9 h-9 rounded-md bg-white/15 border border-white/25 text-white flex items-center justify-center hover:bg-white/25 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-lg"></i>
        </button>
      </div>
    </section>
  );
}